import React, { useState, useCallback, useEffect } from 'react';
import { JobSource, SearchResult, BulkAssignResponse } from '../types';

interface BulkAssignSectionProps {
  bulkAssignUrl: string;
  searchJobSourcesUrl: string;
  prefectureId: number;
  prefectureName: string;
  csrfToken: string;
}

const BulkAssignSection: React.FC<BulkAssignSectionProps> = ({
  bulkAssignUrl,
  searchJobSourcesUrl,
  prefectureId,
  prefectureName,
  csrfToken,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [jobSources, setJobSources] = useState<JobSource[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [filterAssigned, setFilterAssigned] = useState<'all' | 'assigned' | 'unassigned'>('all');

  const searchJobSources = useCallback(async (page: number = 1, term: string = '', filter: 'all' | 'assigned' | 'unassigned' = 'all') => {
    setIsLoading(true);
    setMessage(null);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        prefecture_id: prefectureId.toString(),
        q: JSON.stringify({ name_cont: term }),
      });
      if (filter !== 'all') {
        params.append('filter', filter);
      }
      const response = await fetch(`${searchJobSourcesUrl}?${params.toString()}`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
      });

      if (!response.ok) {
        throw new Error('検索に失敗しました');
      }

      const data: SearchResult = await response.json();
      
      setJobSources(data.job_sources);
      setCurrentPage(data.pagination.current_page);
      setTotalPages(data.pagination.total_pages);
      setTotalCount(data.pagination.total_count);

      // 既に付与されているエージェントを選択状態にする
      const assignedIds = new Set(
        data.job_sources.filter((js) => js.assigned).map((js) => js.id)
      );
      setSelectedIds((prev) => {
        const newSet = new Set(prev);
        assignedIds.forEach((id) => newSet.add(id));
        return newSet;
      });
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : '検索に失敗しました' });
    } finally {
      setIsLoading(false);
    }
  }, [searchJobSourcesUrl, csrfToken, prefectureId]);

  useEffect(() => {
    searchJobSources(1, searchTerm, filterAssigned);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filterAssigned]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    searchJobSources(1, searchTerm, filterAssigned);
  };

  const handleFilterChange = (filter: 'all' | 'assigned' | 'unassigned') => {
    setFilterAssigned(filter);
    setCurrentPage(1);
    searchJobSources(1, searchTerm, filter);
  };

  const handleToggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === jobSources.length) {
      setSelectedIds(new Set());
    } else {
      const allIds = new Set(jobSources.map((js) => js.id));
      setSelectedIds(allIds);
    }
  };

  const handleBulkAssign = async () => {
    if (selectedIds.size === 0) {
      setMessage({ type: 'error', text: 'エージェントが選択されていません' });
      return;
    }

    setIsAssigning(true);
    setMessage(null);

    try {
      const formData = new FormData();
      Array.from(selectedIds).forEach((id) => {
        formData.append('job_source_ids[]', id.toString());
      });

      const response = await fetch(bulkAssignUrl, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json',
        },
        body: formData,
      });

      const data: BulkAssignResponse = await response.json();

      if (data.success) {
        setMessage({
          type: 'success',
          text: data.message || `${data.success_count}件のエージェントに都道府県を付与しました`,
        });
        // 検索結果を更新して、付与状態を反映
        searchJobSources(currentPage, searchTerm, filterAssigned);
      } else {
        setMessage({
          type: 'error',
          text: data.errors?.join(', ') || '一括付与に失敗しました',
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : '一括付与に失敗しました',
      });
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <div className="alert alert-info">
          <strong>付与する都道府県:</strong> {prefectureName}
        </div>
        <h5>エージェント検索</h5>
        <form onSubmit={handleSearch} className="mb-3">
          <div className="row g-2">
            <div className="col-md-8">
              <input
                type="text"
                className="form-control"
                placeholder="エージェント名で検索..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <button type="submit" className="btn btn-primary w-100" disabled={isLoading}>
                {isLoading ? '検索中...' : '検索'}
              </button>
            </div>
          </div>
        </form>
        <div className="mb-3">
          <label className="form-label">フィルター:</label>
          <div className="btn-group" role="group">
            <button
              type="button"
              className={`btn btn-sm ${filterAssigned === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => handleFilterChange('all')}
            >
              すべて
            </button>
            <button
              type="button"
              className={`btn btn-sm ${filterAssigned === 'assigned' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => handleFilterChange('assigned')}
            >
              付与済みのみ
            </button>
            <button
              type="button"
              className={`btn btn-sm ${filterAssigned === 'unassigned' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => handleFilterChange('unassigned')}
            >
              未付与のみ
            </button>
          </div>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show`} role="alert">
          {message.text}
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage(null)}
            aria-label="Close"
          />
        </div>
      )}

      {jobSources.length > 0 && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <span className="text-muted">
                全{totalCount}件中 {((currentPage - 1) * 100) + 1}-{Math.min(currentPage * 100, totalCount)}件を表示
              </span>
              <span className="ms-3">
                選択中: <strong>{selectedIds.size}</strong>件
              </span>
            </div>
            <div>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary me-2"
                onClick={handleSelectAll}
              >
                {selectedIds.size === jobSources.length ? 'すべて解除' : 'すべて選択'}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleBulkAssign}
                disabled={isAssigning || selectedIds.size === 0}
              >
                {isAssigning ? '付与中...' : '選択したエージェントに付与'}
              </button>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.size === jobSources.length && jobSources.length > 0}
                      onChange={handleSelectAll}
                      className="form-check-input"
                    />
                  </th>
                  <th>ID</th>
                  <th>エージェント名</th>
                  <th>正式名称</th>
                  <th>状態</th>
                </tr>
              </thead>
              <tbody>
                {jobSources.map((jobSource) => (
                  <tr key={jobSource.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(jobSource.id)}
                        onChange={() => handleToggleSelect(jobSource.id)}
                        className="form-check-input"
                      />
                    </td>
                    <td>{jobSource.id}</td>
                    <td>
                      <a
                        href={`/admin/job_sources/${jobSource.id}/edit`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary text-decoration-none"
                      >
                        {jobSource.name}
                      </a>
                    </td>
                    <td>
                      {jobSource.official_name ? (
                        <a
                          href={`/admin/job_sources/${jobSource.id}/edit`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary text-decoration-none"
                        >
                          {jobSource.official_name}
                        </a>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      {jobSource.assigned && (
                        <span className="badge bg-success">付与済み</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <nav aria-label="ページネーション">
              <ul className="pagination justify-content-center">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => {
                      setCurrentPage(currentPage - 1);
                      searchJobSources(currentPage - 1, searchTerm, filterAssigned);
                    }}
                    disabled={currentPage === 1 || isLoading}
                  >
                    前へ
                  </button>
                </li>
                <li className="page-item disabled">
                  <span className="page-link">
                    {currentPage} / {totalPages}
                  </span>
                </li>
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => {
                      setCurrentPage(currentPage + 1);
                      searchJobSources(currentPage + 1, searchTerm, filterAssigned);
                    }}
                    disabled={currentPage === totalPages || isLoading}
                  >
                    次へ
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </>
      )}

      {!isLoading && jobSources.length === 0 && searchTerm && (
        <div className="alert alert-info">
          検索結果が見つかりませんでした。
        </div>
      )}
    </div>
  );
};

export default BulkAssignSection;

